/**
 * UI Store (Zustand)
 * Manages global UI state
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { STORAGE_KEYS } from "@/constants";

interface UIState {
  sidebarOpen: boolean;
  /** Phone/tablet (below md) navigation drawer -- separate from the desktop
   *  sidebar so crossing the breakpoint never mounts the modal drawer open. */
  mobileNavOpen: boolean;
  theme: "light" | "dark";
  selectedCampusId: string | null; // null = "All Campuses"
  collapsedSections: Record<string, boolean>; // section name -> collapsed
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleMobileNav: () => void;
  setMobileNavOpen: (open: boolean) => void;
  setTheme: (theme: "light" | "dark") => void;
  setSelectedCampusId: (campusId: string | null) => void;
  toggleSection: (section: string) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      mobileNavOpen: false,
      theme: "light",
      selectedCampusId: null,
      collapsedSections: {
        Operations: true,
        Billing: true,
        Insights: true,
        Admin: true,
      },

      toggleSidebar: () =>
        set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      toggleMobileNav: () =>
        set((state) => ({ mobileNavOpen: !state.mobileNavOpen })),

      setMobileNavOpen: (open) => set({ mobileNavOpen: open }),

      setTheme: (theme) => set({ theme }),

      setSelectedCampusId: (campusId) => set({ selectedCampusId: campusId }),

      toggleSection: (section) =>
        set((state) => ({
          collapsedSections: {
            ...state.collapsedSections,
            [section]: !state.collapsedSections[section],
          },
        })),
    }),
    {
      name: STORAGE_KEYS.UI_STORAGE,
      // Neither drawer flag is persisted: each session starts with the desktop
      // sidebar open and the phone menu closed (see Sidebar.tsx).
      partialize: (state) => ({
        theme: state.theme,
        selectedCampusId: state.selectedCampusId,
        collapsedSections: state.collapsedSections,
      }),
    },
  ),
);
